/**
 * Facebook Tracking Utility
 * 
 * Handles reading Facebook tracking cookies (_fbc, _fbp) for Meta Conversions API integration.
 * The _fbc cookie contains the Facebook Click ID (fbclid) when users click Meta ads.
 * 
 * Cookie format: fb.{subdomain}.{timestamp}.{fbclid}
 * Example: fb.1.1554763741205.IwAR2F4-dbP0l7Mn1IawQQGCINEz7PYXQvwjNwB_qa2ofrHyiLjcbCRxTDMgk
 */

export const FacebookTracking = {
  /**
   * Extracts the fbclid from the _fbc cookie
   * @returns The fbclid string or null if not found
   */
  getFbclid(): string | null {
    try {
      const fbcCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_fbc='));
      
      if (!fbcCookie) {
        console.debug('[FacebookTracking] _fbc cookie not found');
        return null;
      }
      
      const fbcValue = fbcCookie.split('=')[1];
      const parts = fbcValue.split('.');
      
      // Format: fb.{subdomain}.{timestamp}.{fbclid}
      if (parts.length >= 4 && parts[0] === 'fb') {
        const fbclid = parts.slice(3).join('.'); // Get fbclid part (may contain dots)
        console.debug('[FacebookTracking] fbclid extracted:', fbclid.substring(0, 20) + '...');
        return fbclid;
      }
      
      console.warn('[FacebookTracking] Invalid _fbc cookie format:', fbcValue);
      return null;
    } catch (error) {
      console.error('[FacebookTracking] Error reading _fbc cookie:', error);
      return null;
    }
  },

  /**
   * Gets the full _fbc cookie value for Conversions API
   * @returns The complete _fbc cookie value or null if not found
   */
  getFbcCookie(): string | null {
    try {
      const fbcCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_fbc='));
      
      if (!fbcCookie) {
        console.debug('[FacebookTracking] _fbc cookie not found');
        return null;
      }
      
      const fbcValue = fbcCookie.split('=')[1];
      console.debug('[FacebookTracking] _fbc cookie found:', fbcValue.substring(0, 30) + '...');
      return fbcValue;
    } catch (error) {
      console.error('[FacebookTracking] Error reading _fbc cookie:', error);
      return null;
    }
  },

  /**
   * Gets the _fbp cookie value (Facebook Browser ID)
   * @returns The _fbp cookie value or null if not found
   */
  getFbpCookie(): string | null {
    try {
      const fbpCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_fbp='));
      
      if (!fbpCookie) {
        console.debug('[FacebookTracking] _fbp cookie not found');
        return null;
      }
      
      const fbpValue = fbpCookie.split('=')[1];
      console.debug('[FacebookTracking] _fbp cookie found:', fbpValue.substring(0, 30) + '...');
      return fbpValue;
    } catch (error) {
      console.error('[FacebookTracking] Error reading _fbp cookie:', error);
      return null;
    }
  },

  /**
   * Checks if user came from a Facebook ad (has fbclid)
   * @returns True if _fbc cookie exists with valid fbclid
   */
  isFromFacebookAd(): boolean {
    return this.getFbclid() !== null;
  },

  /**
   * Gets tracking data for debugging/logging
   * @returns Object with all available Facebook tracking data
   */
  getTrackingData() {
    return {
      fbclid: this.getFbclid(),
      fbc: this.getFbcCookie(),
      fbp: this.getFbpCookie(),
      isFromAd: this.isFromFacebookAd(),
      timestamp: Date.now()
    };
  },

  /**
   * Formats fbclid for Conversions API payload
   * @param fbclid The raw fbclid value
   * @param timestamp Optional timestamp (defaults to current time)
   * @returns Formatted fbc value for Conversions API
   */
  formatFbcForAPI(fbclid: string, timestamp?: number): string {
    const ts = timestamp || Date.now();
    // Format: fb.{subdomain}.{timestamp}.{fbclid}
    // Using subdomain index 1 for app.hubflo.ai
    return `fb.1.${ts}.${fbclid}`;
  }
};

// Export individual functions for convenience
export const {
  getFbclid,
  getFbcCookie,
  getFbpCookie,
  isFromFacebookAd,
  getTrackingData,
  formatFbcForAPI
} = FacebookTracking; 